import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore } from '@/store';
import Text from '@/components/Typography/Text';
import MessageContent from '@/components/Messages/MessageContent';
import MessageActions from '@/components/Messages/MessageActions';
import MessageBotAvatar from '@/components/Messages/MessageBotAvatar';
import useMessageAgents from '@/hooks/useMessageAgents';
import { MESSAGE_LAYOUT } from '@/constants/config';
import { shouldShowMessageTimestamp } from '@/constants/utils/message';

const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp || Date.now());
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMessageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfMessageDay) / 86400000);
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 0) return time;
  if (diffDays === 1) return `${time} · Yesterday`;
  if (diffDays <= 3) return `${time} · ${date.toLocaleDateString([], { weekday: 'short' })}`;
  return `${time} · ${date.toLocaleDateString([], { day: 'numeric', month: 'short' })}`;
};

const createStyles = (colors) => StyleSheet.create({
  responseRow: {
    flexDirection: 'row',
    width: '100%',
    minWidth: 0,
  },
  responseBody: {
    flex: 1,
    minWidth: 0,
  },
  container: {
    position: 'relative',
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: colors.border_color_secondary,
    marginTop: 5,
    marginBottom: 3,
    paddingLeft: 13,
    paddingVertical: 2,
  },
  marker: {
    position: 'absolute',
    left: -3,
    top: 10,
    width: 6,
    height: 6,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
    backgroundColor: colors.text_color_tertiary,
  },
  timestamp: {
    opacity: 0.5,
    marginTop: 4,
  },
  actions: {
    marginTop: 4,
  },
});

/** A live Work note: transparent enough to follow, quiet enough not to read as the result. */
function WorkProgressUpdate({ conversationId, message, transient = false }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { agents, agentIds } = useMessageAgents(conversationId, message);
  const showTimestamps = useUIStore(state => state.showTimestamps);
  const showTime = !transient && shouldShowMessageTimestamp(message, { isUser: false, showTimestamps });

  return (
    <View style={styles.responseRow}>
      <MessageBotAvatar agent={agents[0]} />
      <View style={[styles.container, styles.responseBody]}>
        <View style={styles.marker} />
        <MessageContent
          conversationId={conversationId}
          messageId={message.id}
          content={message.content}
          citations={message.citations}
          products={message.products}
          stockQuotes={message.stockQuotes}
          places={message.places}
          map={message.map}
          messageLayout={MESSAGE_LAYOUT.SLIM}
          isUser={false}
          isDeleted={message.flags?.isDeleted}
          isStreaming={!!(message.isStreaming || message.isTyping)}
          modelIds={message.modelIds}
          agentIds={agentIds}
        />
        {showTime && (
          <Text size="xs" style={styles.timestamp}>
            {formatMessageTime(message.timestamp)}
          </Text>
        )}
        {!transient && <View style={styles.actions}>
          <MessageActions
            isUser={false}
            isVisible
            showAttribution
            workActions="chrome"
          />
        </View>}
      </View>
    </View>
  );
}

WorkProgressUpdate.propTypes = {
  conversationId: PropTypes.string,
  transient: PropTypes.bool,
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string,
    timestamp: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
    ]),
    citations: PropTypes.array,
    products: PropTypes.array,
    stockQuotes: PropTypes.array,
    places: PropTypes.array,
    map: PropTypes.object,
    modelIds: PropTypes.array,
    agentIds: PropTypes.array,
    flags: PropTypes.object,
    isStreaming: PropTypes.bool,
    isTyping: PropTypes.bool,
  }).isRequired,
};

export default React.memo(WorkProgressUpdate);
